import { Fragment, ReactNode } from 'react';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

export const CHART_COLORS_LIGHT = ['#0B1D2E', '#3B6EA5', '#6FA8DC', '#A3C2E0', '#D1E0F0', '#8FB4D9', '#4A7BB0', '#243B53'];
export const CHART_COLORS_DARK = ['#D1E0F0', '#A3C2E0', '#6FA8DC', '#3B6EA5', '#8FB4D9', '#4A7BB0', '#5E8FC0', '#B8CFE6'];

/** Stable colour per opening method, so the same series is the same colour on every screen. */
export const TRIGGER_COLORS: Record<string, string> = {
  keypad: '#3B6EA5',
  remoto: '#6FA8DC',
  boton: '#D97706',
  manual: '#B45309',
  sistema: '#94A3B8',
  automatico: '#CBD5E1',
};

export const TRIGGER_LABELS: Record<string, string> = {
  keypad: 'Teclado',
  remoto: 'App / Nuki Web',
  boton: 'Botón del Nuki',
  manual: 'Manual (pomo o llave)',
  sistema: 'Sistema',
  automatico: 'Automático',
};

export function useChartTheme() {
  const isDark = useIsDarkMode();
  return {
    isDark,
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    bar: isDark ? '#D1E0F0' : '#0B1D2E',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
    palette: isDark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT,
  };
}

export function madridDay(offsetDays = 0): string {
  return new Date(Date.now() - offsetDays * 86_400_000)
    .toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
}

export function dayLabel(day: string): string {
  const [, m, d] = day.split('-');
  return `${d}/${m}`;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTHS[Number(m) - 1] ?? m} ${y.slice(2)}`;
}

export const PRESETS = [
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
  { label: '6 meses', days: 180 },
  { label: 'Todo', days: 400 },
];

export function RangoSelector({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden">
      {PRESETS.map((p) => (
        <button
          key={p.days}
          onClick={() => onChange(p.days)}
          className={`px-3 py-1.5 text-sm transition-colors ${
            days === p.days
              ? 'bg-sereno-dark text-white'
              : 'bg-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function Panel({ title, hint, children, action }: { title: string; hint?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h2 className="font-semibold text-sereno-dark">{title}</h2>
        {action}
      </div>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-2">{hint}</p>}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 sm:p-4">
      <div className="text-slate-500 text-xs sm:text-sm">{label}</div>
      <div className="text-xl sm:text-2xl font-bold text-sereno-dark mt-0.5 sm:mt-1 break-words">{value}</div>
      {hint && <div className="text-[11px] sm:text-xs text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export interface HeatCell {
  weekday: number;
  hour: number;
  total: number;
  media: number;
}

/** Monday-first display order over the API's 0=Sunday..6=Saturday index. */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_NAMES = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

/**
 * 24×7 clock. The colour encodes `media` — openings on an average Monday at 18h
 * — not the raw count, because a range with five Mondays and four Tuesdays would
 * otherwise paint Monday hotter for no behavioural reason.
 */
export function HeatmapSemanal({ cells, unidad = 'aperturas' }: { cells: HeatCell[]; unidad?: string }) {
  const { isDark } = useChartTheme();
  const max = Math.max(...cells.map((c) => c.media), 0.01);

  const byKey = new Map(cells.map((c) => [`${c.weekday}-${c.hour}`, c]));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        <div className="grid gap-px" style={{ gridTemplateColumns: `28px repeat(24, minmax(0, 1fr))` }}>
          <div />
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="text-[10px] text-slate-400 text-center pb-1">
              {h % 3 === 0 ? h : ''}
            </div>
          ))}

          {WEEKDAY_ORDER.map((wd) => (
            <Fragment key={wd}>
              <div className="text-[11px] text-slate-400 pr-1 flex items-center justify-end">
                {WEEKDAY_NAMES[wd]}
              </div>
              {Array.from({ length: 24 }, (_, h) => {
                const cell = byKey.get(`${wd}-${h}`);
                const intensity = cell ? cell.media / max : 0;
                return (
                  <div
                    key={`${wd}-${h}`}
                    title={`${WEEKDAY_NAMES[wd]} ${String(h).padStart(2, '0')}:00 — ${cell?.media ?? 0} ${unidad} de media (${cell?.total ?? 0} en total)`}
                    className="aspect-square rounded-[2px]"
                    style={{
                      backgroundColor: intensity === 0
                        ? (isDark ? '#ffffff08' : '#0B1D2E08')
                        : isDark
                          ? `rgba(209, 224, 240, ${0.12 + intensity * 0.88})`
                          : `rgba(11, 29, 46, ${0.08 + intensity * 0.92})`,
                    }}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
